import { assign, createMachine } from "xstate";
import {
  IInitialSettings,
  setSimCardSettings,
} from "@nju/data-access-portal-sim-card";
import { pushNumberSettingsEvent } from "@nju/data-layer";
import { BasicError } from "@nju/result";

interface IContext {
  productId: string | undefined;
  isAcrActive: IInitialSettings["isAcrActive"];
  isRoamingActive: IInitialSettings["isRoamingActive"];
  isVoiceMailActive: IInitialSettings["isVoiceMailActive"];
  formError?: string;
  initialSettings?: IInitialSettings;
}

type IEvent =
  | { type: "ERROR" }
  | { type: "SUCCESS" }
  | { type: "SAVE_SETTINGS" }
  | { type: "SET_ACR"; value: boolean }
  | { type: "SET_ROAMING"; value: boolean }
  | { type: "SET_VOICE_MAIL"; value: boolean };

export const mySimCardSettingsMachine = createMachine(
  {
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    initial: "ready",
    states: {
      ready: {
        on: {
          SAVE_SETTINGS: {
            target: "validate",
          },
          SET_ACR: {
            actions: "setAcr",
          },
          SET_ROAMING: {
            actions: "setRoaming",
          },
          SET_VOICE_MAIL: {
            actions: "setVoiceMail",
          },
        },
      },
      validate: {
        entry: "validateCheckbox",
        always: [
          {
            cond: "hasNoError",
            target: "setSimCardSettings",
          },
          {
            target: "ready",
          },
        ],
      },
      setSimCardSettings: {
        invoke: {
          src: "setSimCardSettings",
        },
        on: {
          SUCCESS: {
            actions: "pushDataLayers",
            target: "success",
          },
          ERROR: {
            target: "error",
          },
        },
      },
      success: {
        type: "final",
      },
      error: {
        type: "final",
      },
    },
  },
  {
    actions: {
      pushDataLayers: assign<IContext, IEvent>((context, event) => {
        const {
          isAcrActive,
          isRoamingActive,
          isVoiceMailActive,
          initialSettings,
        } = context;

        if (event.type === "SUCCESS") {
          if (initialSettings?.isAcrActive !== isAcrActive) {
            pushNumberSettingsEvent({
              eventName: "restrictednumbers",
              status: isAcrActive,
            });
          }
          if (initialSettings?.isRoamingActive !== isRoamingActive) {
            pushNumberSettingsEvent({
              eventName: "roaming",
              status: isRoamingActive,
            });
          }
          if (initialSettings?.isVoiceMailActive !== isVoiceMailActive) {
            pushNumberSettingsEvent({
              eventName: "voicemail",
              status: isVoiceMailActive,
            });
          }
        }
        return context;
      }),
      setAcr: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_ACR") {
          return {
            isAcrActive: event.value,
            formError: undefined,
          };
        }
        return context;
      }),
      setRoaming: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_ROAMING") {
          return {
            isRoamingActive: event.value,
            formError: undefined,
          };
        }
        return context;
      }),
      setVoiceMail: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_VOICE_MAIL") {
          return {
            isVoiceMailActive: event.value,
            formError: undefined,
          };
        }
        return context;
      }),
      validateCheckbox: assign<IContext, IEvent>({
        formError: ({
          isAcrActive,
          isRoamingActive,
          isVoiceMailActive,
          initialSettings,
        }) => {
          if (
            initialSettings?.isAcrActive === isAcrActive &&
            initialSettings?.isRoamingActive === isRoamingActive &&
            initialSettings?.isVoiceMailActive === isVoiceMailActive
          ) {
            return "Nie wprowadzono żadnych zmian. Zmień ustawienia i zapisz zmiany.";
          }
          return;
        },
      }),
    },
    guards: {
      hasNoError: ({ formError }) => {
        return formError === undefined;
      },
    },
    services: {
      setSimCardSettings: ({
        productId,
        isAcrActive,
        isRoamingActive,
        isVoiceMailActive,
      }) => {
        return async (callback) => {
          if (!productId) {
            throw new BasicError({
              message: "Missing required fields productId",
            });
          }

          const result = await setSimCardSettings({
            productId,
            isAcrActive,
            isRoamingActive,
            isVoiceMailActive,
          });

          if (result.isError) {
            return callback({ type: "ERROR" });
          }

          return callback({ type: "SUCCESS" });
        };
      },
    },
  },
);
